import React, { memo } from 'react';

import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { styled } from '@/stitches.config';
import type { IParts } from '@/types/shared/parts';

const GENUINE_PARTS_IMAGE = {
  isuzuGenuineParts: {
    src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltbf769fffd316d336/62cfe175b351a936905200e8/isuzu-genuine-parts.svg',
    alt: 'Isuzu Genuine Parts',
    width: 124,
    Height: 48,
  },
  triPetchGenuineParts: {
    src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt576a4fdcd8c7bbd8/62cfe187b5fe93376864889d/tri-petch-genuine-parts.svg',
    alt: 'Tri Petch Genuine Parts',
    width: 124,
    Height: 48,
  },
};

const GenuineParts = styled(Grid, {
  border: '1px solid $gray300',
  br: '$rounded-2',
  textAlign: 'center',
  linearGradient: '$gradientLight',
  variants: {
    size: {
      1: {
        py: '$space-3',
        flex: '0 0 50%',
        maxWidth: '100px',
        '@sm': {
          maxWidth: '124px',
          py: '$space-2',
        },
      },
      2: {
        py: '$space-3',
        flex: '0 0 50%',
        maxWidth: '112px',
        '@sm': {
          maxWidth: '136px',
          py: '$space-2',
        },
      },
      3: {
        py: '$space-3',
        px: '$space-3',
        flex: '0 0 100%',
        maxWidth: 'max-content',
        '@sm': {
          px: '$space-4',
          py: '$space-2',
        },
      },
      '1sm': {
        py: '$space-3',
        flex: '0 0 50%',
        maxWidth: '100px',
      },
      '2sm': {
        py: '$space-3',
        flex: '0 0 50%',
        maxWidth: '112px',
      },
      '3sm': {
        py: '$space-3',
        px: '$space-3',
        flex: '0 0 100%',
        maxWidth: 'max-content',
      },
    },
  },
  defaultVariants: {
    size: '1',
  },
});

const GenuinePartsLogoStyled = styled(Flex, {
  '& > *': {
    maxHeight: '100%',
  },
  variants: {
    largeSize: {
      true: {
        height: '$size-8',
        '@sm': {
          height: '$size-12',
        },
      },
      false: {
        height: '$size-8',
      },
    },
  },
  defaultVariants: {
    largeSize: true,
  },
});
interface PartsGenuineItemProps {
  genuinePartsItem: IParts['genuinePartsItem'];
  size?: 'small' | undefined;
  dataTest?: string;
}

interface GenuinePartsProps {
  isSmall: boolean;
  dataTest: string;
  isIsuzuGenuineParts: boolean;
}

const GenuinePartsLogo = ({
  isSmall,
  dataTest,
  isIsuzuGenuineParts,
}: GenuinePartsProps) => {
  const IMAGE = isIsuzuGenuineParts
    ? GENUINE_PARTS_IMAGE.isuzuGenuineParts
    : GENUINE_PARTS_IMAGE.triPetchGenuineParts;
  return (
    <GenuinePartsLogoStyled
      alignItems="center"
      justifyContent="center"
      data-test={`${dataTest}_genuineParts_logo`}
      largeSize={!isSmall}
    >
      <Image
        src={IMAGE.src}
        alt={IMAGE.alt}
        width={124}
        height={48}
        layout="responsive"
      />
    </GenuinePartsLogoStyled>
  );
};

export const PartsGenuineItem = memo(
  ({
    genuinePartsItem = [],
    size,
    dataTest = 'popup_partsDetail',
  }: PartsGenuineItemProps) => {
    const isSmall = size === 'small';
    return (
      <Flex
        columnGap="2"
        css={!isSmall ? { '@sm': { columnGap: '$space-4' } } : undefined}
        data-test={dataTest + '_genuineParts'}
      >
        {genuinePartsItem.map((item, index) => {
          const isIsuzuGenuineParts = !!item.isuzuGenuineParts;
          return (
            <React.Fragment key={index}>
              {item.year ? (
                <GenuineParts
                  rowGap="2"
                  size={isSmall ? '3sm' : '3'}
                  css={!isSmall ? { '@sm': { rowGap: '$space-1' } } : undefined}
                  data-test={`${dataTest}_genuineParts_item${index + 1}`}
                >
                  <Grid
                    columnGap={{ '@xs': '1' }}
                    alignItems="center"
                    justifyContent="center"
                    css={{
                      gridTemplateColumns: '84px auto',
                      '@xs': {
                        gridTemplateColumns: '112px auto',
                      },
                    }}
                  >
                    <GenuinePartsLogo
                      isSmall={isSmall}
                      isIsuzuGenuineParts={isIsuzuGenuineParts}
                      dataTest={dataTest}
                    />
                    <Box>
                      <Text
                        as="p"
                        size={
                          isSmall
                            ? 'text-caption'
                            : {
                                '@initial': 'text-caption',
                                '@sm': 'sm',
                              }
                        }
                        variant="text-black"
                        font="bold"
                        data-test={dataTest + '_genuineParts_title'}
                      >
                        {isIsuzuGenuineParts
                          ? 'อะไหล่แท้อีซูซุ'
                          : 'อะไหล่แท้ตรีเพชร'}
                      </Text>
                    </Box>
                  </Grid>

                  <Grid
                    columnGap="2"
                    justifyContent="center"
                    css={{
                      gridTemplateColumns: 'max-content auto',
                      textAlign: 'left',
                    }}
                  >
                    {item.year.map((sub, i) => (
                      <React.Fragment key={i}>
                        <Text
                          as="p"
                          size={
                            isSmall
                              ? 'text-caption'
                              : {
                                  '@initial': 'text-caption',
                                  '@sm': 'sm',
                                }
                          }
                          variant="text-black"
                          font="bold"
                          data-test={`${dataTest}_genuineParts_year`}
                        >
                          {sub.label}
                        </Text>
                        <Flex justifyContent="between" columnGap="1">
                          {!!item.startPrice && (
                            <Text
                              as="p"
                              variant="text-red"
                              size={
                                isSmall
                                  ? 'text-caption'
                                  : {
                                      '@initial': 'text-caption',
                                      '@sm': 'sm',
                                    }
                              }
                              font="bold"
                              data-test={`${dataTest}_genuineParts_startPriceLabel`}
                            >
                              เริ่มต้น
                            </Text>
                          )}
                          <Text
                            as="p"
                            variant="text-red"
                            size={
                              isSmall
                                ? 'text-caption'
                                : {
                                    '@initial': 'text-caption',
                                    '@sm': 'sm',
                                  }
                            }
                            font="bold"
                            data-test={`${dataTest}_genuineParts_price`}
                          >
                            {sub.price} บาท
                          </Text>
                        </Flex>
                      </React.Fragment>
                    ))}
                  </Grid>
                </GenuineParts>
              ) : (
                <GenuineParts
                  rowGap="1"
                  size={
                    item.startPrice
                      ? isSmall
                        ? '2sm'
                        : '2'
                      : isSmall
                        ? '1sm'
                        : '1'
                  }
                  data-test={`${dataTest}_genuineParts_item${index + 1}`}
                >
                  <GenuinePartsLogo
                    isIsuzuGenuineParts={isIsuzuGenuineParts}
                    isSmall={isSmall}
                    dataTest={dataTest}
                  />
                  <Box paddingX="1">
                    <Text
                      as="p"
                      size={
                        isSmall
                          ? 'text-caption'
                          : {
                              '@initial': 'text-caption',
                              '@sm': 'sm',
                            }
                      }
                      variant="text-black"
                      font="bold"
                      data-test={`${dataTest}_genuineParts_title`}
                    >
                      {isIsuzuGenuineParts
                        ? 'อะไหล่แท้อีซูซุ'
                        : 'อะไหล่แท้ตรีเพชร'}
                    </Text>
                    <Text
                      as="p"
                      size={
                        isSmall
                          ? 'text-caption'
                          : {
                              '@initial': 'text-caption',
                              '@sm': 'sm',
                            }
                      }
                      variant="text-red"
                      font="bold"
                      wrap
                      dangerouslySetInnerHTML={{
                        __html: `${item.startPrice && 'เริ่มต้น'} <span>${
                          item.price
                        } บาท</span>`,
                      }}
                      data-test={`${dataTest}_genuineParts_price`}
                    />
                  </Box>
                </GenuineParts>
              )}
            </React.Fragment>
          );
        })}
      </Flex>
    );
  }
);

PartsGenuineItem.displayName = 'SectionPartsItemPartsGenuineItem';
